import { useState, useEffect } from 'react'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeContext'
import { DeployVoteWatcher } from './components/DeployVoteWatcher'
import AuthLogin from './components/AuthLogin'
import { TrocaSenhaObrigatoria } from './components/TrocaSenhaObrigatoria'
import { FinSidebar, FIN_LABELS, FiMenu, type FinView } from './components/FinSidebar'
import { CategoriasFinanceiras } from './components/CategoriasFinanceiras'
import { CentrosDeCusto } from './components/CentrosDeCusto'
import { Favorecidos } from './components/Favorecidos'
import { ContasCobranca } from './components/ContasCobranca'
import { CobrancaPanel } from './components/CobrancaPanel'
import { BoletosGerados } from './components/BoletosGerados'
import { CampanhasPanel } from './components/CampanhasPanel'
import { IncentivosPanel } from './components/IncentivosPanel'
import { AtivasSemanaPanel } from './components/AtivasSemanaPanel'
import { VanguardPanel } from './components/VanguardPanel'
import { DesafioPlanilhaPanel } from './components/DesafioPlanilhaPanel'
import { ConversasPanel, FATURAMENTO_DEVICE_ID, EVENTO_CHAMAR, type ChamarPedido } from './components/ConversasPanel'
import { Contas } from './components/Contas'
import { DadosPanel } from './components/DadosPanel'
import { ExtratoMovimentacoes } from './components/ExtratoMovimentacoes'
import { ConferenciaCaixa } from './components/ConferenciaCaixa'
import { FluxoCaixa } from './components/FluxoCaixa'
import { RelatoriosHub } from './components/RelatoriosHub'
import { Historico } from './components/Historico'
import { DdaPanel } from './components/DdaPanel'
import { ArquivosLab } from './components/ArquivosLab'
import { FiLoader } from 'react-icons/fi'
import { NEU as neu } from './lib/neu'

// Casca do Módulo Financeiro (PRD). A navegação é uma SIDEBAR LATERAL (pedido do
// diretor), não o header superior das demais stacks. As telas ainda não têm
// conteúdo — cada seção mostra um placeholder.
function MainApp() {
  const { user, isLoading, hasDeviceAccess, hasFolderAccess, authenticatedFetch } = useAuth()
  // Painel vem do ?panel= da URL (deep link / aba nova); default é fluxo-caixa.
  // Sem isso a navegação era só estado em memória — a barra de endereços nunca
  // mudava, então não havia endereço pra "abrir em nova aba" (pedido do diretor
  // 2026-07-21, mesmo mecanismo já existente no crm).
  const [active, setActive] = useState<FinView>(() => {
    if (typeof window === 'undefined') return 'fluxo-caixa'
    const p = new URLSearchParams(window.location.search).get('panel')
    // FIN_LABELS é Record<FinView, string> — exaustivo por tipo, então serve de
    // lista de painéis válidos sem uma segunda lista pra sair de sincronia.
    return p && Object.prototype.hasOwnProperty.call(FIN_LABELS, p) ? (p as FinView) : 'fluxo-caixa'
  })
  // Reflete a navegação na URL. replaceState (não push) pra não encher o
  // histórico do navegador com um item por clique de menu.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const u = new URLSearchParams(window.location.search)
    u.set('panel', active)
    // PRESERVAR O FRAGMENTO. Ele carrega o handoff SSO (#sso=<token>) na troca
    // de ambiente, e o AuthContext lê isso de forma assíncrona: reescrever a URL
    // sem o hash apagava o token antes da leitura e o destino caía na tela de
    // login. Quem consome o #sso é que o limpa, depois de adotá-lo.
    window.history.replaceState(null, '', `${window.location.pathname}?${u}${window.location.hash}`)
  }, [active])
  // Deep-link da Cobrança → painel Conversas, auto-selecionando a conversa do
  // cliente clicado. Limpado depois que o inbox seleciona.
  const [pendingConversationId, setPendingConversationId] = useState<string | null>(null)
  // Botão "Chamar" do painel Incentivos (24/09): abre as Conversas no número
  // Atendimento, já na conversa da pessoa. O painel avisa por evento de janela
  // porque o botão mora fundo na tabela — mesmo idioma do 'asaas-boleto-send'.
  const [pendingChamar, setPendingChamar] = useState<ChamarPedido | null>(null)
  useEffect(() => {
    const h = (e: Event) => {
      const d = (e as CustomEvent<ChamarPedido>).detail
      if (!d?.codigo) return
      setPendingChamar(d)
      setActive('conversas')
    }
    window.addEventListener(EVENTO_CHAMAR, h)
    return () => window.removeEventListener(EVENTO_CHAMAR, h)
  }, [])
  const [collapsed, setCollapsed] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  // Ação global "Novo registro" (§5.11) removida em 2026-07-14 junto com o
  // header desktop — Contas e Extrato têm seus próprios botões de criação.

  // Badge "precisa responder" no item Conversas da sidebar (pedido do diretor
  // 2026-07-10): nº de conversas ATIVAS com mensagem não lida no device
  // Faturamento. Poll de 30s + refetch ao focar a aba e ao trocar de tela
  // (o socket em tempo real vive só dentro do inbox — aqui polling basta).
  const [waUnread, setWaUnread] = useState(0)
  useEffect(() => {
    if (!user || !hasDeviceAccess(FATURAMENTO_DEVICE_ID)) return
    let dead = false
    const fetchUnread = async () => {
      try {
        const r = await authenticatedFetch(`/api/whatsapp-unread/${FATURAMENTO_DEVICE_ID}`)
        const d = await r.json()
        if (!dead) setWaUnread(Number(d?.active?.count ?? d?.unreadCount ?? 0))
      } catch { /* mantém o último valor */ }
    }
    fetchUnread()
    const iv = setInterval(fetchUnread, 30_000)
    window.addEventListener('focus', fetchUnread)
    return () => { dead = true; clearInterval(iv); window.removeEventListener('focus', fetchUnread) }
  }, [user, hasDeviceAccess, authenticatedFetch, active])

  if (isLoading) {
    return (
      <div className="min-h-screen w-full bg-background flex items-center justify-center">
        <FiLoader className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
      </div>
    )
  }

  if (!user) return <AuthLogin />
  // Mesma trava do CRM: com a troca pendente o app não monta.
  if (user.mustChangePassword) return <TrocaSenhaObrigatoria />

  // Gate de acesso ao APP (pedido do dono 2026-07-10): o SCF é restrito à
  // permissão explícita `app_financeiro` do crm — SEM bypass de admin. O
  // backend já barra tudo com 403; esta tela só troca os erros crus por um
  // aviso claro. Se permissions não carregou (falha transitória), deixa
  // passar — o backend continua sendo a muralha.
  const perms = (user as any)?.permissions
  const hasFinAccess = !perms || !!perms?.toolPermissions?.some(
    (t: any) => t.tool_name === 'app_financeiro' && (t.can_access === 1 || t.can_access === true))
  if (!hasFinAccess) {
    return (
      <div className="min-h-screen w-full bg-background flex items-center justify-center p-6">
        <div className="glass-panel rounded-2xl p-8 max-w-md text-center space-y-3">
          <img src="/favicon.svg" alt="SCF" className="h-10 w-10 mx-auto opacity-80" draggable={false} />
          <h2 className="text-lg font-semibold text-foreground">Sem acesso ao SCF</h2>
          <p className="text-sm text-muted-foreground">
            Seu usuário não tem permissão pro Sistema de Controle Financeiro.
            Se precisar, peça a liberação a um administrador (Configurações do CRM, seção Aplicativos).
          </p>
          <button
            onClick={() => { localStorage.clear(); window.location.href = 'https://crm.twms.com.br' }}
            className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium px-4 py-2 hover:opacity-90"
          >
            Ir pro CRM
          </button>
        </div>
      </div>
    )
  }

  return (
    /* .neu-stage: campo neu na RAIZ, pra que o vão ao redor da sidebar descolada
       seja o mesmo campo em que os cartões flutuam, e não o creme do dotted-bg.
       É também o PORTÃO da sidebar solta — as regras de .neu-side-float pedem
       este ancestral, então sem NEU a barra volta rente sem prop nenhuma. */
    <div className={`flex h-[100dvh] dotted-bg ${neu ? 'neu-stage' : ''} text-foreground overflow-hidden`}>
      <FinSidebar
        active={active}
        onNavigate={setActive}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed(c => !c)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        waUnread={waUnread}
      />

      <div className="flex-1 flex flex-col min-w-0">
        {/* ── Header desktop removido em TODOS os painéis (pedido 2026-07-14):
            todo o espaço vertical é do conteúdo. O "Novo registro" global saiu
            junto — Contas e Extrato têm seus próprios botões de criação.
            No mobile (<lg) fica este mini-header (hambúrguer + título), senão
            o celular perde a navegação; no Conversas nem isso — o hambúrguer
            vive na barra do device do inbox. */}
        {!(active === 'conversas' && hasDeviceAccess(FATURAMENTO_DEVICE_ID)) && (
        <header className="lg:hidden h-14 shrink-0 flex items-center gap-2 px-3 border-b border-border bg-card">
          <button
            onClick={() => setMobileOpen(true)}
            title="Menu"
            aria-label="Abrir menu"
            className="flex items-center justify-center w-10 h-10 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary"
          >
            <FiMenu className="w-5 h-5" />
          </button>
          <h1 className="text-base font-semibold text-foreground truncate">{FIN_LABELS[active]}</h1>
        </header>
        )}

        {/* ── Conteúdo da seção ──
            Box GLOBAL (regra 2026-07-14): 30px de respiro responsivo em todos os
            lados (p-4 no mobile), um único cartão com sombra, e o scroll é INTERNO
            ao box — a página não rola (main overflow-hidden). Painéis de altura-
            cheia (Cobrança/Conversas/Arquivos, que têm layout próprio + overlays
            `fixed`) preenchem o box direto; os demais fluem numa região com
            overflow-y-auto. */}
        {/* Só o painel Dados é full-bleed abaixo de 1280px — os demais seguem
            com o encaixotado padrão. Um cartão com respiro de cada lado rouba
            32px de uma tela de 390 e faz a conversa parecer caixa dentro de
            caixa. Mesmo ajuste feito no App do crm. */}
        <main className={`flex-1 min-h-0 overflow-hidden ${active === 'dados' ? 'p-0 xl:p-[30px]' : 'p-4 sm:p-[30px]'}${neu ? ' neu-lab neu-backdrop' : ''}`}>
          <div className={`h-full ${active === 'dados' ? 'xl:rounded-xl' : 'rounded-xl'} card-shadow bg-card overflow-hidden${neu ? ' neu-canvas' : ''}`}>
          {/* 'dados' entrou na lista de altura cheia (01/09/2026): abaixo de
              1280px o painel virou uma tela de conversa, e dentro de um
              overflow-y-auto ele ganhava uma SEGUNDA barra de rolagem — a
              conversa rolava por dentro e a página rolava por fora, levando o
              campo de digitação para fora da tela. */}
          {/* pln-auto (21/10/2026): régua PLANOS — chão da marca + blocos de papel
              (fim do index.css). Vai AQUI, no ponto único de montagem, e não em
              cada painel. Vale também para Conversas, Dados e Arquivos: lá as
              regras de escopo próprio (.pln-conversas/.pln-dados/.pln-arquivos)
              vêm por cima, e a camada genérica limpa o que elas não cobrem (a
              barra de devices do inbox do fin, que o crm não tem). EXCETO o
              Dados: lá ela clareava a caixa de SQL (texto claro sobre fundo
              claro) e apagava as colunas — fica só com o .pln-dados, como no crm. */}
          <div className={`${
            (active === 'cobranca' || (active === 'conversas' && hasDeviceAccess(FATURAMENTO_DEVICE_ID)) || active === 'arquivos' || active === 'dados' || active === 'incentivos' || active === 'ativas-semana' || active === 'vanguard' || active === 'desafio')
              ? 'h-full' : 'h-full overflow-y-auto'
          }${neu && active !== 'dados' ? ' pln-auto' : ''}`}>
          {active === 'cad-categorias' ? (
            <CategoriasFinanceiras />
          ) : active === 'cad-centros-custo' ? (
            <CentrosDeCusto />
          ) : active === 'cad-favorecidos' ? (
            <Favorecidos />
          ) : active === 'cad-conta-pj' ? (
            <ContasCobranca />
          ) : active === 'dados' ? (
            <DadosPanel />
          ) : active === 'pagar' ? (
            <Contas kind="despesa" />
          ) : active === 'cobranca' ? (
            <CobrancaPanel onOpenConversation={id => { setPendingConversationId(id); setActive('conversas') }} />
          ) : active === 'conversas' && hasDeviceAccess(FATURAMENTO_DEVICE_ID) ? (
            <ConversasPanel
              openConversationId={pendingConversationId}
              onOpenHandled={() => setPendingConversationId(null)}
              chamar={pendingChamar}
              onChamarHandled={() => setPendingChamar(null)}
              onOpenMenu={() => setMobileOpen(true)}
            />
          ) : active === 'campanhas' ? (
            <CampanhasPanel onOpenConversation={id => { setPendingConversationId(id); setActive('conversas') }} />
          ) : active === 'incentivos' ? (
            user.isAdmin
              ? <IncentivosPanel />
              : <div className="p-6 text-sm text-muted-foreground">Painel restrito aos administradores.</div>
          ) : active === 'vanguard' ? (
            user.isAdmin
              ? <VanguardPanel />
              : <div className="p-6 text-sm text-muted-foreground">Painel restrito aos administradores.</div>
          ) : active === 'desafio' ? (
            user.isAdmin
              ? <DesafioPlanilhaPanel />
              : <div className="p-6 text-sm text-muted-foreground">Painel restrito aos administradores.</div>
          ) : active === 'ativas-semana' ? (
            user.isAdmin
              ? <AtivasSemanaPanel />
              : <div className="p-6 text-sm text-muted-foreground">Painel restrito aos administradores.</div>
          ) : active === 'boletos' ? (
            <BoletosGerados />
          ) : active === 'movimentacoes' ? (
            <ExtratoMovimentacoes />
          ) : active === 'conferencia-caixa' ? (
            <ConferenciaCaixa />
          ) : active === 'fluxo-caixa' ? (
            <FluxoCaixa />
          ) : active === 'relatorios' ? (
            <RelatoriosHub onNavigate={setActive} />
          ) : active === 'historico' ? (
            <Historico />
          ) : active === 'dda' ? (
            <DdaPanel />
          ) : active === 'arquivos' ? (
            // Painel Arquivos — cópia do crm. É altura-cheia: preenche o box global.
            (user.isAdmin || hasFolderAccess)
              ? <ArquivosLab authenticatedFetch={authenticatedFetch} />
              : <div className="p-6 text-sm text-muted-foreground">Você ainda não tem pastas liberadas — peça o acesso no crm.twms.</div>
          ) : (
            <div className="h-full flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 border border-primary/20">
                  <img src="/favicon.svg" alt="" className="h-8 w-8 opacity-80" draggable={false} />
                </div>
                <h2 className="text-xl font-semibold text-foreground">{FIN_LABELS[active]}</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Tela do Módulo Financeiro ainda em construção. A navegação lateral já
                  está pronta — vamos preencher esta seção a seguir.
                </p>
              </div>
            </div>
          )}
          </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <MainApp />
        <DeployVoteWatcher />
      </AuthProvider>
    </ThemeProvider>
  )
}
